import "../budgets/css/BudgetList.css"
import React, { useEffect, useState, useCallback } from "react"
import { Card } from "react-bootstrap"
import { Link, useNavigate } from "react-router-dom"
import { message, Spin, Select } from "antd"

import DataTableComponent from "../../components/DataTable/DataTable.js"
import Layout from "../../layout/default"
import Block from "../../components/Block/Block"
import budgetService from "./services/budgetService.ts"

const { Option } = Select
const PAGE_SIZE = 10

const STATUS_COLOR_MAP = {
  "": "#6c757d", // Sin estado
  enviado: "#0d6efd",
  aceptado: "#198754",
  denegado: "#dc3545",
  caducado: "#fd7e14",
}

const linkStyle = {
  color: "#0d6efd",
  textDecoration: "underline",
  fontWeight: "500",
}

const BudgetList = () => {
  const navigate = useNavigate()

  const [budgetData, setBudgetData] = useState([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [updatingBudgetId, setUpdatingBudgetId] = useState(null)

  const [statusOptions, setStatusOptions] = useState([])
  const [statusMap, setStatusMap] = useState({})
  const [statusColorLookup, setStatusColorLookup] = useState({})

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const response = await budgetService.getBudgetStateList()
        const opts = response.data
        const idToLabelMap = {}
        const labelToColorMap = {}

        opts.forEach((o) => {
          const lower = (o.state || "").toLowerCase()
          idToLabelMap[o.id] = o.state
          labelToColorMap[lower] =
            STATUS_COLOR_MAP[lower] || STATUS_COLOR_MAP[""]
        })

        setStatusOptions(opts)
        setStatusMap(idToLabelMap)
        setStatusColorLookup(labelToColorMap)
      } catch (error) {
        console.error("Error fetching status options:", error)
        message.error("Error al cargar opciones de estado")
      }
    }
    fetchStatusOptions()
  }, [])

  const handleBackendFilter = useCallback(
    async (pageNumber = 1, filters = {}) => {
      setLoading(true)
      const processed = { ...filters }

      // --- MODIFIED STATE FILTER LOGIC ---
      if (processed.state && typeof processed.state === "string") {
        const searchTerm = processed.state.toLowerCase().trim()

        // Find the first status option that starts with the search term.
        const matchedOption = statusOptions.find((opt) =>
          opt.state.toLowerCase().startsWith(searchTerm)
        )

        if (matchedOption) {
          // If a match is found, use its ID for the filter.
          processed.state = matchedOption.id
        } else {
          // If no match, remove the state filter to avoid sending an invalid value.
          delete processed.state
        }
      }

      try {
        const response = await budgetService.getBudgetListPaginated({
          ...processed,
          page: pageNumber,
          page_size: PAGE_SIZE,
        })

        if (response && Array.isArray(response.results)) {
          setBudgetData(response.results)
          setCount(response.count)
          setPage(pageNumber)
        } else {
          setBudgetData([])
          setCount(0)
        }
      } catch (error) {
        console.error("Error al cargar presupuestos:", error)
        setBudgetData([])
        setCount(0)
      } finally {
        setLoading(false)
      }
    },
    [statusOptions] // Dependency updated to statusOptions
  )

  useEffect(() => {
    handleBackendFilter(1, {})
  }, [handleBackendFilter])

  const paginateBudgetList = useCallback(
    (pageNumber = 1, filters = {}) => {
      handleBackendFilter(pageNumber, filters)
    },
    [handleBackendFilter]
  )

  const handleStatusChange = async (newStatusId, budgetId, originalBudget) => {
    setUpdatingBudgetId(budgetId)
    try {
      const payload = { ...originalBudget, state: newStatusId }
      delete payload.state_label
      delete payload.total
      delete payload.iva_total
      delete payload.grand_total
      delete payload.lawyer_net
      delete payload.system_net
      delete payload.lawyer_name
      delete payload.business_name
      delete payload.items
      delete payload.procedures

      await budgetService.updateBudget(budgetId, payload)
      message.success("Estado actualizado correctamente.")
      handleBackendFilter(page, {})
    } catch (error) {
      console.error("Error al cambiar el estado del presupuesto:", error)
      message.error("Error al actualizar el estado.")
    } finally {
      setUpdatingBudgetId(null)
    }
  }

  const budgetColumns = [
    {
      name: "Presupuesto",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div style={{ fontWeight: "500" }}>
          <div style={{ fontSize: "11px", color: "#888" }}>ID: {row.id}</div>
          {row.name}
        </div>
      ),
    },
    {
      name: "Importe (€)",
      selector: (row) => Number(row.grand_total ?? 0),
      sortable: true,
      right: true,
      cell: (row) => (
        <strong>
          {Number(row.grand_total ?? 0).toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{" "}
          €
        </strong>
      ),
    },
    {
      name: "Abogado / Despacho",
      selector: (row) => row.business_name || row.lawyer_name,
      sortable: true,
      cell: (row) => {
        if (!row.lawyer) return "—"
        const displayText =
          row.business_name || row.lawyer_name || `ID: ${row.lawyer}`
        return (
          <Link to={`/lawyer/${row.lawyer}`} style={linkStyle}>
            {displayText}
          </Link>
        )
      },
    },
    {
      name: "Lead",
      selector: (row) => row.lead ?? "—",
      sortable: true,
      center: true,
      cell: (row) => {
        if (!row.lead) return "—"
        return (
          <Link to={`/lead/${row.lead}`} style={linkStyle}>
            {row.lead}
          </Link>
        )
      },
    },
    {
      name: "Estado",
      selector: (row) => statusMap[row.state] || "",
      sortable: true,
      right: true,
      cell: (row) => {
        const stateId = row.state
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            {updatingBudgetId === row.id ? (
              <Spin size="small" />
            ) : (
              <Select
                value={stateId || ""}
                onChange={(value) => handleStatusChange(value, row.id, row)}
                bordered={false}
                style={{ minWidth: "120px" }}
                getPopupContainer={(trigger) => trigger.parentElement}
              >
                {statusOptions.map((opt) => (
                  <Option key={opt.id} value={opt.id}>
                    <span
                      style={{
                        backgroundColor:
                          statusColorLookup[opt.state.toLowerCase()] ||
                          STATUS_COLOR_MAP[""],
                        color: "#fff",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        textTransform: "capitalize",
                      }}
                    >
                      {opt.state}
                    </span>
                  </Option>
                ))}
              </Select>
            )}
          </div>
        )
      },
    },
    {
      name: "Neto Abogado (€)",
      selector: (row) => Number(row.lawyer_net ?? 0),
      sortable: true,
      right: true,
      cell: (row) => {
        const netValue = Number(row.lawyer_net ?? 0)
        if (netValue === 0) {
          return (
            <span style={{ color: "#888", fontStyle: "italic" }}>
              Sin suscripción
            </span>
          )
        }
        return (
          <span style={{ color: "#198754" }}>
            {netValue.toLocaleString("es-ES", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            €
          </span>
        )
      },
    },
    {
      name: "Neto Sistema (€)",
      selector: (row) => Number(row.system_net ?? 0),
      sortable: true,
      right: true,
      cell: (row) => {
        const netValue = Number(row.system_net ?? 0)
        if (netValue === 0) {
          return (
            <span style={{ color: "#888", fontStyle: "italic" }}>
              Sin suscripción
            </span>
          )
        }
        return (
          <span style={{ color: "#dc3545" }}>
            {netValue.toLocaleString("es-ES", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            €
          </span>
        )
      },
    },
  ]

  const customStyles = {
    table: {
      style: {
        borderCollapse: "separate",
        borderSpacing: "1px 8px",
        backgroundColor: "transparent",
      },
    },
    headRow: {
      style: {
        backgroundColor: "transparent",
        borderBottom: "1px solid #e0e0e0",
      },
    },
    headCells: {
      style: {
        color: "#888",
        fontWeight: 600,
        textTransform: "uppercase",
        padding: "0 8px",
      },
    },
    rows: {
      style: {
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        borderRadius: "4px",
      },
    },
    cells: { style: { padding: "10px 8px", color: "#555" } },
  }

  return (
    <Layout title="Presupuestos" content="container" loading={loading}>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Presupuestos</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Presupuestos
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>
      <Block>
        <Card>
          {loading ? (
            <Spin
              tip="Cargando presupuestos..."
              size="large"
              style={{ margin: 24 }}
            />
          ) : (
            <DataTableComponent
              tableClassName="data-table-head-light table-responsive"
              customStyles={customStyles}
              data={budgetData}
              columns={budgetColumns}
              backEndPagination={true}
              fetchPaginatedData={paginateBudgetList}
              pageCount={count}
              currentPaginationPage={page}
              backendFilter={true}
              filterFunc={handleBackendFilter}
              searchByList={["id", "lead", "lawyer", "state"]}
              isLeadForm={false}
              noDataComponent={
                <div style={{ textAlign: "center", padding: 20 }}>
                  No hay presupuestos disponibles.
                </div>
              }
            />
          )}
        </Card>
      </Block>
    </Layout>
  )
}

export default BudgetList
